import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import requests, { ApiError } from '../../api/client';
import { useSession } from '../../Session';
import { useFeedback } from '../common/Feedback';
import { useCountdown } from '../../hooks';
import { interpolate, useTranslation } from '../../i18n';
import { cardSx, colors, primaryButtonSx, radius } from '../../theme/tokens';
import { formatPhone, isValidMobile, onlyDigits } from '../../utils/phone';
import { sanitizePlans } from '../../utils/plans';
import { ROUTES } from '../../utils/routes';
import { OtpInput } from '../otp/OtpInput';
import type { LineDetails } from '../../types/api';

/**
 * Passos 1 e 2 do Fluxo B: informar a linha e validar o código SMS.
 *
 * Sequência (equivalente ao `plano.js` + `PlanoController`):
 *
 * 1. **Identificar a linha.** `GET /linhas/detalhes` confirma que a linha
 *    pertence à marca do hostname. A marca nunca vem da linha digitada: quem
 *    a define é o `x-caller-url`, e uma linha de outra operadora simplesmente
 *    não é encontrada.
 * 2. **Enviar o OTP.** O código é gerado e enviado pelo servidor; o SPA só
 *    recebe um handle opaco (`otp_session_token`).
 * 3. **Validar o OTP.** Em caso de sucesso a API devolve o `linha_token`, que
 *    acompanha a solicitação de recarga. Só então buscamos os planos e
 *    seguimos para a lista.
 */

/** Cooldown de reenvio, igual ao legado. */
const RESEND_COOLDOWN_SECONDS = 120;

type Step = 'phone' | 'otp';

export function LineValidation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startSession } = useSession();
  const { show } = useFeedback();
  const { seconds, start: startCountdown } = useCountdown();

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [resending, setResending] = useState(false);

  const otpSessionToken = useRef<string | null>(null);

  const failWith = useCallback(
    (title: string, text?: string) => {
      show({ kind: 'error', title, text });
    },
    [show]
  );

  /** Passo 1 — identifica a linha e dispara o SMS. */
  const handlePhoneSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setPhoneError(null);

      const digits = onlyDigits(phone);

      if (!digits) {
        setPhoneError(t.home.errors.phoneRequired);
        return;
      }
      if (!isValidMobile(phone)) {
        setPhoneError(t.home.errors.phoneLength);
        return;
      }

      setSubmitting(true);

      try {
        const lookup = await requests.lineLookup(digits);
        if (lookup?.retorno === false) {
          show({
            kind: 'warning',
            title: t.common.attention,
            text: lookup.mensagem || t.home.errors.lineNotFound,
          });
          setPhone('');
          return;
        }

        const otp = await requests.otpStart(digits);
        otpSessionToken.current = otp.otp_session_token;
        setStep('otp');
        startCountdown(otp.cooldown_segundos ?? RESEND_COOLDOWN_SECONDS);
      } catch (error) {
        const apiError = error instanceof ApiError ? error : null;

        // Linha desconhecida chega como 404, mas o middleware nem sempre
        // preserva o status do upstream — daí a checagem também pela mensagem.
        // O importante é nunca mostrar texto técnico ("Not found") ao cliente.
        const isNotFound =
          apiError?.status === 404 || /not found|nao foi possivel validar/i.test(apiError?.message ?? '');

        if (isNotFound) {
          show({ kind: 'warning', title: t.common.attention, text: t.home.errors.lineNotFound });
          setPhone('');
          return;
        }

        if (apiError?.status === 429) {
          show({ kind: 'warning', title: t.common.attention, text: t.otp.errors.cooldown });
          return;
        }

        failWith(t.common.error, apiError?.message || t.home.errors.lineCheckFailed);
      } finally {
        setSubmitting(false);
      }
    },
    [phone, show, failWith, startCountdown, t]
  );

  /** Passo 2 — valida o código e carrega os planos. */
  const handleCodeSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setCodeError(null);

      if (code.length < 6) {
        setCodeError(t.otp.errors.length);
        return;
      }
      if (!otpSessionToken.current) {
        failWith(t.common.error, t.otp.errors.sessionExpired);
        setStep('phone');
        return;
      }

      setValidating(true);

      try {
        const validation = await requests.otpValidate({
          otp_session_token: otpSessionToken.current,
          codigo: code,
        });

        if (validation?.retorno === false) {
          setCodeError(validation.mensagem || t.otp.errors.invalid);
          return;
        }

        const plans = sanitizePlans(await requests.plans());

        startSession({
          linha: onlyDigits(phone),
          detalhes: (validation.detalhes ?? {}) as LineDetails,
          planos: plans,
          linhaToken: validation.linha_token ?? null,
          otpSessionToken: otpSessionToken.current,
        });

        navigate(ROUTES.plans);
      } catch (error) {
        const apiError = error instanceof ApiError ? error : null;
        setCodeError(apiError?.message || t.otp.errors.validateFailed);
      } finally {
        setValidating(false);
      }
    },
    [code, phone, navigate, startSession, failWith, t]
  );

  const handleResend = useCallback(async () => {
    if (!otpSessionToken.current || seconds > 0) return;

    setResending(true);
    try {
      const otp = await requests.otpResend({ otp_session_token: otpSessionToken.current });
      otpSessionToken.current = otp.otp_session_token || otpSessionToken.current;
      startCountdown(otp.cooldown_segundos ?? RESEND_COOLDOWN_SECONDS);
      show({ kind: 'success', title: t.common.success, text: t.otp.resent });
    } catch (error) {
      const apiError = error instanceof ApiError ? error : null;
      failWith(t.common.error, apiError?.message || t.otp.errors.resendFailed);
    } finally {
      setResending(false);
    }
  }, [seconds, startCountdown, show, failWith, t]);

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
      }}
    >
      <Box
        sx={{
          ...cardSx,
          maxWidth: 448,
          width: '100%',
          p: 4,
          borderRadius: radius.xl,
          borderTop: `4px solid ${colors.primary}`,
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
        }}
      >
        {step === 'phone' ? (
          <Box component="form" onSubmit={handlePhoneSubmit} noValidate>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                component="h1"
                sx={{ fontSize: '1.875rem', fontWeight: 800, color: colors.textPrimary }}
              >
                {t.home.title}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: '0.875rem', color: colors.textSecondary }}>
                {t.home.subtitle}
              </Typography>
            </Box>

            <TextField
              id="form_fone"
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
              fullWidth
              required
              label={t.home.phoneLabel}
              placeholder={t.home.phonePlaceholder}
              value={phone}
              onChange={(event) => {
                setPhone(formatPhone(event.target.value));
                setPhoneError(null);
              }}
              error={Boolean(phoneError)}
              helperText={phoneError ?? ' '}
              slotProps={{ htmlInput: { maxLength: 15 } }}
              sx={{ '& .MuiInputBase-input': { fontSize: '1.125rem', py: 2 } }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting}
              endIcon={submitting ? undefined : <ArrowForwardIcon />}
              sx={{ ...primaryButtonSx, py: 2, fontSize: '1.125rem', mt: 1 }}
            >
              {submitting ? <CircularProgress size={26} sx={{ color: '#fff' }} /> : t.home.submit}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleCodeSubmit} noValidate>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                component="h1"
                sx={{ fontSize: '1.875rem', fontWeight: 800, color: colors.textPrimary }}
              >
                {t.otp.title}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: '0.875rem', color: colors.textSecondary }}>
                {interpolate(t.otp.subtitle, { phone })}
              </Typography>
            </Box>

            <OtpInput
              value={code}
              onChange={(next) => {
                setCode(next);
                setCodeError(null);
              }}
              onComplete={() => {
                // Sem auto-submit: o usuário confirma clicando, igual ao legado.
              }}
              error={codeError}
              label={t.otp.codeLabel}
              placeholder={t.otp.codePlaceholder}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={validating}
              sx={{ ...primaryButtonSx, py: 2, fontSize: '1.125rem', mt: 2 }}
            >
              {validating ? <CircularProgress size={26} sx={{ color: '#fff' }} /> : t.otp.submit}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                type="button"
                onClick={handleResend}
                disabled={seconds > 0 || resending}
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: colors.primary,
                  '&.Mui-disabled': { color: colors.primary, opacity: 0.5 },
                }}
              >
                {seconds > 0 ? interpolate(t.otp.resendCountdown, { seconds }) : t.otp.resend}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
